
import {
    Component,
    Input,
    OnInit,
    effect,
    inject,
    input,
    model,
    output,
} from '@angular/core';
import { ACCOUNT_ROLE_DOMAINS, AppPermissions, AuthenticationService, IRole } from '@my-aon/auth';
import { ENVIRONMENT } from '@environment';
import { TranslateService } from '@ngx-translate/core';
import {
    InterceptedError,
    NotifyService,
    SanitizerWrapperService,
    SharedModule,
    StoreService,
} from '@my-aon/shared';
import { hasValue, mapFormAddTranslatedLabels, validateGroup, DxFormItem } from '@my-aon/utils';
import { PermissionViewModel, RolesService } from '../../../lib';
import { RolePermissionsListComponent } from '../role-permissions-list/role-permissions-list.component';

@Component({
    selector: 'app-form-role-details',
    templateUrl: './form-role-details.component.html',
    styleUrl: './form-role-details.component.scss',
    standalone: true,
    imports: [
        SharedModule,
        RolePermissionsListComponent
    ],
})
export class FormRoleDetailsComponent implements OnInit {

    readonly environment = inject(ENVIRONMENT);
    readonly translateService: TranslateService = inject(TranslateService);
    readonly storeService: StoreService = inject(StoreService);
    readonly auth: AuthenticationService = inject(AuthenticationService);
    readonly rolesService = inject(RolesService);
    readonly notify = inject(NotifyService);
    readonly sanitizer = inject(SanitizerWrapperService);

    $submited = model<boolean>(false, { alias: 'submited' });

    onClose = output<IRole | null>();

    $data = model.required<IRole>({ alias: 'data' });
    formData: IRole | null = null;

    permissions: PermissionViewModel[] = [];

    originalData: IRole | null = null;

    $readOnly = model.required<boolean>({ alias: 'readOnly' });

    $validationGroupName = input<string>('FormRoleDetailsComponent' + 'ValidationGroupName', { alias: 'validationGroupName' });
    validate = async () => await validateGroup(this.$validationGroupName());

    get isNew() {
        return !hasValue(this.formData?.Id);
    }

    loadIndicatorVisible: boolean = true;

    // Form items
    formItems: DxFormItem[] = [];

    constructor() {
        effect(() => {
            const data = this.$data();

            if (data && hasValue(data)) {
                if (hasValue(data?.Id)) this.reload();
                else {
                    setTimeout(() => {
                        this.regenerateModel(data);
                    }, 23);
                }
            }
        });

        effect(() => {
            this.$submited() && this.formSubmit();
        });
    }

    ngOnInit(): void {
        this.validate();
    }

    reload() {
        this.loadIndicatorVisible = true;
        this.rolesService.byKey(this.$data()?.Id).subscribe({
            next: (model: IRole) => {
                this.regenerateModel(model);
            },
        });
    }

    async regenerateModel(model: IRole) {
        this.formData = model;
        this.originalData = { ...model };

        let readOnly = !this.auth.hasAnyPermission(
            AppPermissions.SYS_AUTH_ROLES_MANAGE_ROLES
        );

        // Prevent changing SysAdmin role.
        if (this.originalData.SystemRole && this.originalData.Domain == ACCOUNT_ROLE_DOMAINS.SYS)
            readOnly = true;

        // Prevent changing own major role

        /* @TODO
         if (this.originalData.Domain <= userMinimumPriority)
             readOnly = true; */

        if (readOnly != this.$readOnly())
            this.$readOnly.set(readOnly);


        this.permissions = this.rolesService.$permissions().map((x) => {
            let active: boolean = false;
            if (
                this.originalData?.Domain == ACCOUNT_ROLE_DOMAINS.SYS &&
                this.originalData?.SystemRole == true
            )
                active = true;
            // SysAdmin has all permissions even if not explicitly set.
            else
                active = this.formData?.PermissionKey?.some((y) => y.Key == x.key) ?? false;
            return new PermissionViewModel({ ...x, active: active });
        });

        this.formItems = [
            {
                dataField: 'Name',
                isRequired: true,
                colSpan: 8,
                editorOptions: {
                    readOnly: !this.isNew,//(this.$readOnly() || this.formData?.SystemRole),
                },
            },
            {
                dataField: 'Domain',
                colSpan: 4,
                editorOptions: { readOnly: true },
            },
        ].map(
            mapFormAddTranslatedLabels(this.translateService, 'ADMIN.ROLES.FIELD')
        );

        this.validate();

        this.loadIndicatorVisible = false;
    }

    formSubmitTrigger: boolean = false;
    formSubmit = async (e?: any) => {
        if (this.loadIndicatorVisible)
            //already submited
            return;

        if (!(await this.validate())) {
            this.$submited.set(false);
            return;
        }

        this.submit();
    };

    private submit = async () => {
        this.loadIndicatorVisible = true;

        const cancel = () => {
            this.$submited.set(false);
            this.loadIndicatorVisible = false;
        };

        const created = (newRole: IRole) => {
            this.close(newRole);
        };

        if (!hasValue(this.formData?.Id)) {
            delete (this.formData as any)?.Id; // Prevent Id = Null from breaking API until refactor is done to send regular json instead of FormData.
        }

        // Make permission changes effective
        this.formData!.PermissionKey = this.permissions
            .filter((x) => x.active)
            .map((x) => {
                return { Key: x.key };
            });

        return this.rolesService
            .insertOrUpdate(this.formData!, this.originalData!)
            .subscribe({
                next: (data) => {
                    created(data);
                },
                error: (error: InterceptedError) => {
                    console.error(error);
                    const message = error.humanizeError(this.sanitizer);
                    console.error(message)
                    this.notify.error(message, 'Error while submitting Role');
                    cancel();
                },
            });
    };

    close = (pcp: IRole) => {
        this.onClose.emit(pcp ? pcp : this.formData);
    };

}
